
import {ComputedRef, defineComponent, PropType, ref, Ref} from "vue";
import {computed} from "@vue/reactivity";
import {useRoute, useRouter} from "vue-router";
import {useStore} from "vuex";
import {Filter} from "../../../../../domain/entities/shop/filter/filter";
import {Prices} from "../../../../../domain/entities/shop/filter/prices";
import {Category} from "../../../../../domain/entities/shop/filter/category";
import {Shop} from "../../../../../domain/entities/shop/filter/shop";
import {dispatchErrorBox} from "../../mixins";
import {RepositoryException} from "../../../../../domain/exceptions/exceptions";

export default defineComponent({
  name: "filterShop",
  props: {
    filter: {
      type: Object as PropType<Filter>,
      required: true
    }
  },

  setup: function(props) {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    /** Array used for listing prices with in the filter and to send the entity in the request. */
    const arrayPrices: Array<Prices> = [
      new Prices(1, "Até 10", 0, 10),
      new Prices(2, "10-100", 10, 100),
      new Prices(3, "100-1000", 100, 1000),
      new Prices(4, "Acima de 1000", 1000, 0)
    ];

    /** Variable that gets the current page through the route. */
    const currentPage: Ref<number> = ref(Number(route.params.productsPage));

    /** Variable responsible for opening and closing the filter in mobile format. */
    const open: Ref<boolean> = ref(false);

    /** Computed which controls the current state of the filter.
     * In this variable, all filters are grouped.
     * To load the products that are in this variable it is necessary to call the variable through the store. */
    const currentFilter: ComputedRef<Filter> = computed(() => {
      return store.getters["shop_module/currentFilter"];
    });

    /** List of selectable filter entities. */
    const filters: ComputedRef<Filter> = computed(() => {
      return props.filter;
    });

    /** Variable that receives the lowest value of the filter input. */
    const minPrice: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/minPrice"];
    });

    /** Variable that receives the lowest value of the filter input. */
    const maxPrice: ComputedRef<number> = computed(() => {
      return store.getters["shop_module/maxPrice"];
    });

    /** Function responsible for setting the amount of products available per entity. */
    function getQuantityByPrice(priceID: number): number {
      switch (priceID) {
        case 1:
          return filters.value.values.firstValue;

        case 2:
          return filters.value.values.secondValue;

        case 3:
          return filters.value.values.thirtyValue;

        case 4:
          return filters.value.values.fourthValue;
      }
      return 0;
    }

    /** Function responsible for opening and closing the filter in mobile format. */
    function select(): void {
      open.value = !open.value;
    }

    /** Function that receives as a parameter the minimum value typed in the input.
     * Then this value is set in the store. */
    async function selectMinInputPrice(input: HTMLInputElement): Promise<void> {
      try {
        await store.dispatch("shop_module/setMinPrice", Number(input.value));
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function that receives as a parameter the maximum value typed in the input.
     * Then this value is set in the store. */
    async function selectMaxInputPrice(input: HTMLInputElement): Promise<void> {
      try {
        await store.dispatch("shop_module/setMaxPrice", Number(input.value));
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for updating the URL with the values set in the inputs. */
    async function submit(): Promise<void> {
      await updateRouteQuery();
    }

    /** Function responsible for getting the selected price inputs and checking if they are checked or unchecked.
     * If the input is checked, the URL is updated with the entity's id.
     * If the input is unchecked the entity id is removed from the URL. */
    async function selectPrice(
      priceEntity: Prices,
      input: HTMLInputElement
    ): Promise<void> {
      const inputID: string = input.value;

      if (input.checked) {
        const filtered = arrayPrices.filter(e => String(e.id) === inputID);

        if (filtered.length > 0) {
          const price = filtered[0];
          currentFilter.value.prices.push(price);

          await loadFilters(currentFilter.value);
          await store.dispatch(
            "shop_module/currentFilter",
            currentFilter.value
          );
          await updateRouteQuery();
        }
      } else {
        const newFilter = new Array<Prices>();

        for (const it of currentFilter.value.prices) {
          if (String(it.id) !== inputID) {
            newFilter.push(it);
          }
        }

        currentFilter.value.prices.splice(0, currentFilter.value.prices.length);
        currentFilter.value.prices.push(...newFilter);

        await loadFilters(currentFilter.value);

        await updateRouteQuery();
      }
    }

    async function loadFilters(filter: Filter): Promise<void> {
      try {
        await store.dispatch("shop_module/loadFilters", filter);
      } catch (e) {
        if (e instanceof RepositoryException) {
          await dispatchErrorBox(e.message);
        } else {
          console.error(e);
          await dispatchErrorBox("Ocorreu um erro inesperado.");
        }
      }
    }

    /** Function responsible for taking the inputs of the selected categories and checking if they are checked or unchecked.
     * If the input is checked, the URL is updated with the entity's id.
     * If the input is unchecked the entity id is removed from the URL. */
    async function selectCategories(input: HTMLInputElement): Promise<void> {
      const inputID: string = input.value;

      if (input.checked) {
        const filtered = props.filter.categories.filter(
          e => String(e.id) === inputID
        );

        if (filtered.length > 0) {
          const category = filtered[0];
          currentFilter.value.categories.push(category);

          await loadFilters(currentFilter.value);
          await store.dispatch(
            "shop_module/currentFilter",
            currentFilter.value
          );

          await updateRouteQuery();
        }
      } else {
        const newFilter = new Array<Category>();

        for (const it of currentFilter.value.categories) {
          if (String(it.id) !== inputID) {
            newFilter.push(it);
          }
        }

        currentFilter.value.categories.splice(
          0,
          currentFilter.value.categories.length
        );
        currentFilter.value.categories.push(...newFilter);

        await loadFilters(currentFilter.value);

        await updateRouteQuery();
      }
    }

    /** Function responsible for getting the inputs from the selected stores and checking if they are checked or unchecked.
     * If the input is checked, the URL is updated with the entity's id.
     * If the input is unchecked the entity id is removed from the URL. */
    async function selectShops(input: HTMLInputElement): Promise<void> {
      const inputID: string = input.value;

      if (input.checked) {
        const filtered = props.filter.shops.filter(
          e => String(e.id) == inputID
        );
        if (filtered.length > 0) {
          const shop = filtered[0];
          currentFilter.value.shops.push(shop);

          await loadFilters(currentFilter.value);
          await store.dispatch(
            "shop_module/currentFilter",
            currentFilter.value
          );

          await updateRouteQuery();
        }
      } else {
        const newFilter = new Array<Shop>();

        for (const it of currentFilter.value.shops) {
          if (String(it.id) !== inputID) {
            newFilter.push(it);
          }
        }

        currentFilter.value.shops.splice(0, currentFilter.value.shops.length);
        currentFilter.value.shops.push(...newFilter);

        await loadFilters(currentFilter.value);

        await updateRouteQuery();
      }
    }

    /** Updates the route queries merging the queries selected with the current queries if there's any. */
    async function updateRouteQuery(): Promise<void> {
      const currentShops = new Array<string>();
      const currentCategories = new Array<string>();
      const currentPrices = new Array<string>();
      const minValue = new Array<number>();
      const maxValue = new Array<number>();

      for (const it of currentFilter.value.shops) {
        currentShops.push(String(it.id));
      }

      for (const it of currentFilter.value.categories) {
        currentCategories.push(String(it.id));
      }

      for (const it of currentFilter.value.prices) {
        currentPrices.push(String(it.id));
      }

      if (minPrice.value > 0) {
        minValue.push(minPrice.value);
      }

      if (maxPrice.value > 0) {
        maxValue.push(maxPrice.value);
      }

      await router.push({
        name: "shop",
        params: { productsPage: 1 },
        query: {
          shop: [...currentShops],
          category: [...currentCategories],
          price: [...currentPrices],
          min: minValue,
          max: maxValue
        }
      });
    }

    return {
      arrayPrices,
      filters,
      minPrice,
      maxPrice,
      open,
      currentPage,
      selectShops,
      selectCategories,
      selectPrice,
      selectMinInputPrice,
      selectMaxInputPrice,
      select,
      submit,
      getQuantityByPrice
    };
  }
});
